<template>
    <div>
        <VSelect
            v-model="v$.resourceCode.$model"
            :canClear="false"
            :options="configVehicles"
            :hasError="v$.resourceCode.$error"
            placeholder="Select type of vehicle"
            @update:modelValue="onVehicleChange"
        />
        <template v-if="currentVehicle">
            <div v-if="currentVehicle.hasLicensePlate || currentVehicle.showLength || currentVehicle.showFuelType" class="inputs">
                <div v-if="currentVehicle.hasLicensePlate && !v$.rental.$model">
                    <span class="form-label">License plate</span>
                    <VInput v-model="v$.licenseNumber.$model" :hasError="v$.licenseNumber.$error" class="license" />
                </div>

                <div v-if="showFuelType">
                    <span class="form-label">Fuel Type</span>
                    <VSelect
                        v-model="v$.fuelType.$model"
                        :canClear="false"
                        :options="filteredFuelTypes"
                        :hasError="v$.fuelType.$error"
                        placeholder="Select fuel type"
                        class="fuel-type"
                    />
                </div>
                <div v-if="currentVehicle.showLength">
                    <span class="form-label">Total length (M)</span>
                    <VInput v-model="v$.length.$model" :hasError="v$.length.$error" type="tel" />
                </div>
            </div>
            <div v-if="currentVehicle.hasLicensePlate" class="checkbox-container">
                <VCheckbox class="checkbox" v-model="v$.rental.$model" :id="Math.random().toString()" />
                <span>Rental car/Unknown license plate</span>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { Direction } from "@/models/Enums";
import { configVehicles } from "@/modules/Quickform/config/vehicles";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import { computed, reactive } from "vue";
import { VehicleItem } from "../../models/Vehicle";
import { VCheckbox, VInput, VSelect } from "v-ferry-components";
import { fueltypes } from "../../config/fuelTypes";

interface Props {
    vehicleIndex: number;
    vehicle: VehicleItem;
    direction: Direction;
}

const props = defineProps<Props>();

const state = reactive<VehicleItem>({
    resourceCode: props.vehicle.resourceCode ?? "",
    licenseNumber: props.vehicle.licenseNumber ?? "",
    length: props.vehicle.length ?? undefined,
    rental: props.vehicle.rental ?? false,
    fuelType: props.vehicle.fuelType ?? null,
});

const rules = {
    resourceCode: { required },
    licenseNumber: {
        required: requiredIf(function () {
            return !!currentVehicle.value?.hasLicensePlate && !state.rental;
        }),
    },
    length: {
        required: requiredIf(function () {
            return !!currentVehicle.value?.showLength;
        }),
    },
    rental: {},
    fuelType: {},
};

const v$ = useVuelidate(rules, state);

const currentVehicle = computed(() => {
    return configVehicles.find((v) => v.value === state.resourceCode);
});

const filteredFuelTypes = computed(() => {
  if (currentVehicle.value?.value === "BIC") {
    return fueltypes.filter(f => f.value === "E" || f.value === "");
  }
  return fueltypes.filter(f => f.value !== "");
});

const showFuelType = computed(() => {
  return (currentVehicle.value?.showFuelType &&
    (!state.rental || currentVehicle.value?.value === 'BIC')
  );
});

function onVehicleChange() {
  state.fuelType = null;

  if (currentVehicle.value?.value === "BIC") {
    state.licenseNumber = "";
  }
}

defineExpose({
    state,
});
</script>

<style lang="scss" scoped>
.inputs {
    margin: 15px 0;
    @include flex(row, space-between, center, 15px);
    flex-flow: row wrap;
    width: 100%;

    & > * {
        flex: 1;
    }
}

.license {
    &__.v-input {
        &__input {
            height: 50px;
        }
    }
}

.fuel-type {
    width: 100%;
    min-width: 170px;
}

.form-label {
    font-size: 0.875rem;
    color: #404040;
    text-wrap: nowrap;
}

.checkbox-container {
    margin: 10px 0;
    display: flex;
    color: var(--c-black);
}
</style>
